<template>
  <d-container
    fluid
    class="main-content-container px-0 px-md-2 px-lg-4">
    <!-- Page Header -->
    <d-row
      no-gutters
      class="page-header py-4">
      <d-col
        sm="12"
        md="6"
        lg="6"
        class="text-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">Produkty</span>
        <h3 class="page-title">
          <template v-if="isEdit">Edycja produktu {{ data.name }}</template>
          <template v-else>Dodawanie produktu</template>
        </h3>
      </d-col>
      <d-col
        sm="12"
        md="6"
        lg="6"
        class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="secondary"
          @click.prevent="$router.push('/produkty')"
          class="mr-1">
          <i class="material-icons">arrow_back</i>
          Wróć do listy
        </d-button>
        <d-button theme="success" @click.prevent="validateForm">
          Zapisz zmiany
        </d-button>
      </d-col>
    </d-row>

    <!-- Form -->
    <d-form>
      <d-card class="mb-5">
        <div
          v-if="!allDataLoaded"
          class="loading-spinner pb-3 pt-3 text-center">
          <span class="loading-spinner-content text-primary">
            Trwa ładowanie danych&hellip;
          </span>
        </div>
        <validation-observer
          ref="product-form"
          tag="form">
          <d-tabs
            card
            :class="{'is-loading': !allDataLoaded}">
            <!-- Dane ogólne -->
            <d-tab
              title="Dane ogólne"
              active
              class="width-narrow px-3 px-md-4">
              <h4 class="mb-3 pb-3 border-bottom">Dane ogólne</h4>
              <d-row>
                <d-col
                  lg="6"
                  md="6"
                  sm="12">
                  <label class="d-block">Typ produktu</label>
                  <d-form-radio
                    inline
                    value="SIMPLE"
                    :disabled="isEdit"
                    v-model="data.type">
                      Prosty
                  </d-form-radio>
                  <d-form-radio
                    inline
                    value="VARIANT"
                    :disabled="isEdit"
                    v-model="data.type">
                      Z wariantami
                  </d-form-radio>
                </d-col>
              </d-row>

              <div class="form-group">
                <label for="name"> Nazwa produktu: </label>

                <validation-provider
                  rules="required"
                  name="name"
                  v-slot="{ errors }">
                  <d-input
                    :state="errors.length ? 'invalid' : null"
                    v-model="data.name"/>
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div class="form-group">
                <label for="code"> Kod EAN: </label>

                <validation-provider
                  rules="required"
                  name="code"
                  v-slot="{ errors }">
                  <d-input
                    :state="errors.length ? 'invalid' : null"
                    v-model="data.code"/>
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div class="form-group">
                <label for="category">Kategoria produktu:</label>

                <validation-provider
                  name="category"
                  v-slot="{ errors }">
                  <div
                    :class="{
                      'form-control': true,
                      'is-select': true,
                      'is-invalid': errors.length,
                      'vs-wrap-text': true
                    }">
                    <v-select
                      :disabled="!loadedCategories"
                      :options="allCategories"
                      v-model="data.category_id"
                      :reduce="(category) => category.id"
                      placeholder="Wybierz kategorię"
                      :searchable="true"
                      label="name">
                    </v-select>
                  </div>
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div class="form-group">
                <label for="manufacturer">Producent:</label>

                <validation-provider
                  name="manufacturer"
                  v-slot="{ errors }">
                  <div
                    :class="{
                      'form-control': true,
                      'is-select': true,
                      'is-invalid': errors.length,
                      'vs-wrap-text': true
                    }">
                    <v-select
                      :disabled="!loadedManufacturers"
                      :options="allManufacturers"
                      v-model="data.manufacturer_id"
                      :reduce="(manufacturer) => manufacturer.id"
                      placeholder="Wybierz producencenta"
                      :searchable="true"
                      label="name">
                    </v-select>
                  </div>
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <d-row>
                <d-col
                  lg="6"
                  md="6"
                  sm="6">
                  <div class="form-group">
                    <label for="price"> Cena brutto: </label>

                    <validation-provider
                      rules="required|min_value:0.1"
                      type="number"
                      name="price"
                      v-slot="{ errors }">
                      <d-input-group
                        append="zł">
                        <d-input
                          class="text-right"
                          v-model="data.price"/>
                      </d-input-group>
                      <div v-if="errors.length" class="invalid-feedback d-block">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>
                <d-col
                  lg="6"
                  md="6"
                  sm="6">
                  <div class="form-group" append="$">
                    <label for="vat_rate"> Stawka VAT: </label>

                    <validation-provider
                      rules="required"
                      name="vat_rate"
                      v-slot="{ errors }">
                      <div
                        :class="{
                          'form-control': true,
                          'is-select': true,
                          'is-invalid': errors.length,
                          'vs-wrap-text': true
                        }">
                        <v-select
                          v-model="data.vat_rate"
                          :disabled="!allDataLoaded"
                          :options="vatRateOptions"
                          :reduce="(rate) => rate.value"
                          :searchable="false"
                          placeholder="Wybierz stawkę VAT"
                          label="text">
                        </v-select>
                      </div>
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>
              </d-row>

              <d-row v-if="data.type === 'SIMPLE'">
                <d-col
                  lg="12"
                  md="12"
                  sm="12">
                  <div class="form-group">
                    <label for="minimal-stock"> Nie pozwalaj zamawiać gdy w magazynie jest mniej niż: </label>

                    <validation-provider
                      rules="required|min_value:0"
                      type="number"
                      name="minimal-stock"
                      v-slot="{ errors }">
                      <d-input-group
                        append="szt.">
                        <d-input
                          class="text-right"
                          v-model="data.minimal_stock"/>
                        </d-input-group>
                      <div v-if="errors.length" class="invalid-feedback d-block">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>
              </d-row>

              <div
                v-if="data.type === 'VARIANT'"
                class="form-group">
                <label for="variants"> Warianty produktu: </label>

                <validation-provider
                  name="variants"
                  :rules="{ required: data.type === 'VARIANT' }"
                  v-slot="{ errors }">
                  <div
                    :class="{
                      'form-control': true,
                      'is-select': true,
                      'is-invalid': errors.length,
                      'vs-wrap-text': true
                    }">
                    <v-select
                      class=""
                      :options="allVariants"
                      v-model="data.variants"
                      :reduce="(variant) => variant.id"
                      placeholder="Wybierz warianty"
                      :searchable="true"
                      :multiple="true"
                      :disabled="isEdit"
                      label="name">
                    </v-select>
                  </div>
                </validation-provider>
              </div>

              <d-row
                v-if="!data.type === 'VARIANT'"
                class="pt-2 pb-1">
                <d-col
                  lg="6"
                  md="6"
                  sm="12">
                  <d-checkbox
                    toggle
                    toggle-small
                    class="mr-0"
                    inline
                    v-model="data.available_in_package">
                    Dostępny w pakiecie:
                  </d-checkbox>
                </d-col>
              </d-row>

              <d-row
                v-if="!data.type === 'VARIANT' && data.available_in_package"
                class="mb-3">
                <d-col
                  lg="6"
                  md="6"
                  sm="12">
                  <div class="form-group">
                    <label for="package_price"> Cena pakietu (brutto): </label>
                    <validation-provider
                      rules="required|min_value:1"
                      name="package_price"
                      type="number"
                      v-slot="{ errors }">
                      <d-input-group append="zł">
                        <d-input
                          class="text-right"
                          :state="errors.length ? 'invalid' : null"
                          v-model="data.package_price"/>
                      </d-input-group>
                      <div v-if="errors.length" class="invalid-feedback d-block">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>
                <d-col
                  lg="6"
                  md="6"
                  sm="12">
                  <label class="d-block">Liczba sztuk w pakiecie:</label>
                  <d-input-group
                    append="szt.">
                    <d-input
                      class="text-right"
                      :min="2"
                      :disabled="true"
                      v-model="data.quantity_in_package"
                      type="number" />
                  </d-input-group>
                </d-col>
              </d-row>

              <div class="form-group">
                <label for="description"> Opis produktu: </label>

                <validation-provider
                  name="description"
                  v-slot="{ errors }">
                  <d-textarea
                    :state="errors.length ? 'invalid' : null"
                    v-model="data.description"
                    :value.prop="data.description"
                    :rows="6"/>
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <hr>

              <div class="form-group">
                <label>
                  Zdjęcie produktu:
                </label>

                <div class="">
                  <div
                    v-if="data.image_url"
                    class="mb-1 mt-1">
                    <d-img
                      fluid
                      rounded
                      width="100%"
                      height="auto"
                      center
                      style="min-width: 100%;"
                      :src="data.image_url" />
                  </div>

                  <div
                    v-if="data.image_url"
                    class="text-center pt-2 mt-4">
                    <d-link
                      @click.prevent="removeProductImage()"
                      title="Usuń zdjęcie produktu"
                      class="text-danger">
                      Usuń zdjęcie produktu
                    </d-link>
                  </div>

                  <file-input
                    v-if="!data.image_url"
                    :onChange="setProductImage"
                    id="inputFileProductImage" />
                </div>
              </div>

              <div
                v-if="!loaded"
                class="pb-3 pt-3 text-center">
                Trwa ładowanie danych&hellip;
              </div>

              <d-alert
                v-if="loadError"
                show
                theme="warning">
                Wczytywanie danych nie powiodło się.
                <a
                  href="javascript:window.location.reload();"
                  class="alert-link">
                  Odśwież stronę
                </a>
                aby spróbować ponownie.
              </d-alert>
            </d-tab>
            <!-- Warianty -->
            <d-tab
              title="Warianty"
              :disabled="data.type !== 'VARIANT'">
              <h4
                v-if="isEdit"
                class="pb-3 mb-0 border-bottom d-flex flex-sm-wrap">
                Warianty
                <!-- <d-button
                  class="ml-auto"
                  theme="success"
                  @click.prevent="showAddVariant">
                  <i class="material-icons">add</i>
                  Dodaj wariant
                </d-button> -->
              </h4>

              <table class="table mt-0 mb-0 full-width">
                <thead class="bg-light">
                  <tr>
                    <th scope="col" class="border-0 align-middle">Wariant:</th>
                    <th scope="col" class="border-0 align-middle text-center">Cena</th>
                    <th scope="col" class="border-0 align-middle text-center">Dostępność</th>
                    <th scope="col" class="border-0 align-middle text-center" style="width: 150px;">Akcje</th>
                  </tr>
                </thead>
                <tbody v-if="loaded && loadedProductVariants">
                  <tr v-for="(child, index) of data.children" :key="'child-' + index">
                    <td class="align-middle py-lg-1 py-md-3 py-3">
                      <label class="table-mobile-label mb-1">Wariant:</label>
                      <template v-for="(variantItem, subindex) of child.variant_items">
                        <small :key="'variant-item-' + index + '-' + subindex">
                          <strong>{{ variantNames[variantItem.variant_id] }}:</strong> {{ variantItem.name }}
                        </small>
                        <br :key="'variant-item-br-' + index + '-' + subindex">
                      </template>
                    </td>
                    <td class="align-middle text-center py-lg-1 py-md-3 py-3">
                      <label class="table-mobile-label mb-1">Cena</label>
                      {{ roundProperly(child.price) }} zł
                    </td>
                    <td class="align-middle text-center py-lg-1 py-md-3 py-3">
                      <label class="table-mobile-label mb-1">Dostępność</label>
                      <span v-if="child.availability === 1" class="text-success">
                        TAK<br>
                        <small>({{ child.stock }} szt.)</small>
                      </span>
                      <span v-if="child.availability === 0" class="text-danger">
                        NIE
                      </span>
                    </td>
                    <td class="text-center align-middle pt-0">
                      <d-button
                        theme="primary"
                        @click.prevent="showEditVariant(child)"
                        class="mx-2 mt-2"
                        title="Edytuj">
                        <i class="material-icons">edit</i>
                      </d-button>
                      <!-- <d-button
                        theme="danger"
                        :disabled="variantRemoveInProgress"
                        @click.native="removeVariant(child.id)"
                        class="mx-2 mt-2"
                        title="Usuń">
                        <i class="material-icons">delete</i>
                      </d-button> -->
                    </td>
                  </tr>
                  <tr v-if="data.children.length === 0">
                    <td
                      colspan="4"
                      class="text-center">
                      Brak wariantów do wyświetlenia&hellip;
                    </td>
                  </tr>
                </tbody>
              </table>

              <div
                v-if="isEdit && (!loaded || !loadedProductVariants)"
                class="pb-3 pt-3 text-center">
                Trwa ładowanie danych&hellip;
              </div>

              <d-alert
                v-if="loadError"
                show
                theme="warning">
                Wczytywanie danych nie powiodło się.
                <a
                  href="javascript:window.location.reload();"
                  class="alert-link">
                  Odśwież stronę
                </a>
                aby spróbować ponownie.
              </d-alert>
              <d-alert
                v-if="!isEdit"
                show
                theme="warning">
                Dodawanie będzie możliwe po zapisaniu tego produktu.
              </d-alert>
            </d-tab>
          </d-tabs>
        </validation-observer>
      </d-card>
    </d-form>

    <product-variant-popup />
  </d-container>
</template>

<script>
import Vue from 'vue';
import ListUtils from './../../utils/ListUtils.js';
import FormUtils from './../../utils/FormUtils.js';
import CustomDecimals from './../../utils/CustomDecimals.js';
import ProductVariantPopup from './../popups/ProductVariantPopup.vue';

export default {
  name: 'product-form',
  components: {
    'product-variant-popup': ProductVariantPopup,
  },
  computed: {
    allDataLoaded () {
      return this.loaded && this.loadedVariants;
    },
    isEdit () {
      return !!this.$route.params.id;
    },
    variantNames () {
      if (!this.allVariants.length) {
        return {};
      }

      let variantNames = {};

      for (let i = 0; i < this.allVariants.length; i++) {
        let variant = this.allVariants[i];
        variantNames[variant.id] = variant.name;
      }

      return variantNames;
    },
    vatRateOptions() {
      return [
        {
          text: '23%',
          value: 23,
        },
        {
          text: '8%',
          value: 8,
        },
        {
          text: '7%',
          value: 7,
        },
        {
          text: '5%',
          value: 5,
        },
        {
          text: '0%',
          value: 0,
        },
      ];
    },
    availableInPackage () {
      return this.data.available_in_package;
    }
  },
  watch: {
    availableInPackage (newValue) {
      if (newValue) {
        this.data.quantity_in_package = 2;
      } else {
        this.data.quantity_in_package = 0;
      }
    }
  },
  data() {
    return {
      data: {
        id: 0,
        type: 'SIMPLE',
        variants: [],
        parent_id: null,
        variant_items: [],
        name: '',
        code: '',
        price: 0,
        vat_rate: null,
        availability: true,
        image: '',
        image_url: '',
        description: '',
        children: [],
        category_id: null,
        manufacturer_id: null,
        available_in_package: false,
        quantity_in_package: 0,
        package_price: 0,
        minimal_stock: 0
      },
      allVariants: [],
      allManufacturers: [],
      allCategories: [],
      loaded: false,
      loadError: false,
      loadedVariants: false,
      loadedProductVariants: false,
      loadedManufacturers: false,
      loadedCategories: false,
      variantRemoveInProgress: false,
      isValidated: false
    };
  },
  mounted() {
    if (this.isEdit) {
      this.loadItemData();
    } else {
      this.loaded = true;
    }
    this.loadAdditionalData();
    this.$bus.$on('product-variant-added', this.reloadVariants);
  },
  methods: {
    loadItemData() {
      FormUtils.loadItemData(this, {
        method: 'get',
        endpointBase: '/api/products/item/',
        instanceFields: [
          'id',
          'type',
          'variants',
          'parent_id',
          'variant_items',
          'name',
          'code',
          'price',
          'vat_rate',
          'availability',
          'image',
          'image_url',
          'description',
          'children',
          'category_id',
          'manufacturer_id',
          'available_in_package',
          'quantity_in_package',
          'package_price',
          'minimal_stock'
        ],
        booleanFields: ['availability', 'available_in_package'],
        loadedKey: 'loaded',
        successAction: () => {
          this.loadedProductVariants = true;
          Vue.nextTick(() => {
            this.data.available_in_package = !!this.data.available_in_package;
          });
        }
      });
    },
    loadAdditionalData() {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/api/variants/items',
        method: 'get',
        outputKey: 'allVariants',
        loadedKey: 'loadedVariants',
        errorKey: 'loadError',
        params: {
          pagination: 0,
        },
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: '/api/manufacturers/items',
        method: 'get',
        outputKey: 'allManufacturers',
        loadedKey: 'loadedManufacturers',
        errorKey: 'loadError',
        params: {
          pagination: 0,
        }
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: '/api/categories/items',
        method: 'get',
        outputKey: 'allCategories',
        loadedKey: 'loadedCategories',
        errorKey: 'loadError',
        params: {
          pagination: 0,
        }
      });
    },
    validateForm() {
      this.isValidated = true;
      FormUtils.validate(this.$refs['product-form'], this.submitForm);
    },
    submitForm() {
      let imageFields = false;
      let formDataToSend = this.prepareFormDataToSend();
      let endpointUrl = this.isEdit
        ? '/api/products/update/' + this.data.id
        : '/api/products/store';

      if (this.data.image instanceof File) {
        formDataToSend.image = this.data.image;
        imageFields = ['image'];
      } else if (this.data.image) {
        formDataToSend.image = this.data.image;
      } else {
        formDataToSend.image = '';
        formDataToSend.image_url = '';
      }

      FormUtils.submit(this, {
        endpoint: endpointUrl,
        formData: formDataToSend,
        successTitle: 'Zapisano produkt',
        successText: 'Wybrany produkt został zapisany',
        errorTitle: 'Wystąpił błąd',
        errorText: 'Modyfikacja produktu nie powiodła się. Spróbuj ponownie.',
        successAction: () => {
          this.$router.push('/produkty');
        },
      }, imageFields);
    },
    prepareFormDataToSend() {
      let formDataToSend = {
        name: this.data.name,
        code: this.data.code,
        price: this.data.price,
        vat_rate: this.data.vat_rate,
        description: this.data.description,
        category_id: this.data.category_id,
        manufacturer_id: this.data.manufacturer_id,
        available_in_package: this.data.available_in_package ? 1 : 0
      };

      if (!this.isEdit) {
        formDataToSend.type = this.data.type;
      }

      if (!this.isEdit && this.data.type === 'VARIANT') {
        formDataToSend.variants = this.data.variants;
      }

      if (this.data.type === 'CHILD') {
        formDataToSend.parent_id = this.data.parent_id;
      }

      if (this.data.type === 'CHILD' || this.data.type === 'SIMPLE') {
        formDataToSend.minimal_stock = this.data.minimal_stock;
      }

      if (this.data.available_in_package) {
        formDataToSend.quantity_in_package = this.data.quantity_in_package;
        formDataToSend.package_price = this.data.package_price;
      }

      return formDataToSend;
    },
    showAddVariant() {
      this.$bus.$emit(
        'product-variant-popup-show',
        {
          id: this.data.id,
          name: this.data.name,
          vat_rate: this.data.vat_rate,
          variants: this.data.variants
        },
        this.allVariants
      );
    },
    showEditVariant(selectedChild) {
      this.$bus.$emit(
        'product-variant-popup-show',
        {
          id: this.data.id,
          name: this.data.name,
          vat_rate: this.data.vat_rate,
          variants: this.data.variants
        },
        this.allVariants,
        selectedChild,
        true
      );
    },
    reloadVariants() {
      this.loadedProductVariants = false;

      FormUtils.loadItemData(this, {
        method: 'get',
        endpointBase: '/api/products/item/',
        instanceFields: [
          'children'
        ],
        loadedKey: 'loadedProductVariants',
      });
    },
    removeVariant(variantID) {
      this.variantRemoveInProgress = true;

      ListUtils.removeItem(this, {
        endpointBase: '/api/products/delete/',
        id: variantID,
        successTitle: 'Usunięto wariant produktu',
        successText: 'Wybrany wariant produktu został usunięty',
        confirmTitle: 'Czy na pewno chcesz usunąć ten wariant produktu?',
        confirmText: 'Tej operacji nie można cofnąć',
        errorTitle: 'Wystąpił błąd',
        errorText: 'Usuwanie wariantu produktu nie powiodło się. Spróbuj ponownie.',
        successAction: () => {
          this.variantRemoveInProgress = false;
          this.reloadVariants();
        },
        cancelAction: () => {
          this.variantRemoveInProgress = false;
        },
      });
    },
    removeProductImage () {
      this.data.image_url = '';
      this.data.image = '';
    },
    setProductImage (event) {
      this.data.image = event.target.files[0];
    },
    roundProperly (value, decimals = 2) {
      return CustomDecimals.roundProperly(value, decimals);
    }
  },
  beforeDestroy() {
    this.$bus.$off('product-variant-added', this.reloadVariants);
  }
};
</script>
